import { AppLayout, Flashbar, FlashbarProps, Link, MixedLineBarChartProps, SplitPanel, Tabs } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { DFPHomePageSideNavigation } from 'src/components/dfp-home-page/DFPHomePageSideNavigation';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { ChartSummaryPanel } from '../../generic-components/ai-summary-components/ChartSummaryPanel';
import { FinTechOpsHref } from '../FinTechOpsConstants';
import { StatusType } from '../sas/SasInterfaces';
import { UserMetricsContext } from './UserMetricsContext';
import { UserMetricsDetailsTab } from './UserMetricsDetailsTab';
import { UserMetricsHierarchyMetricsTab } from './UserMetricsHierarchyMetricsTab';
import { HIERARCHY_VIEW_MODE } from './UserMetricsConstants';
import {
  UserMetricsAggregatedViewsTable,
  UserMetricsApplicationFootprintContainer,
  UserMetricsMasterFilters,
  UserMetricsParsedData
} from './UserMetricsInterfaces';
import { UserMetricsOverview } from './UserMetricsOverview';
import { UserMetricsSelectionFilters } from './UserMetricsSelectionFilters';
import { DEFAULT_MANAGER_LEVEL } from './UserMetricsConstants';

export const UserMetricsPage: React.FC = () => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [summaryFilters, setSummaryFilters] = useState<string>('');

  //for Page
  const [multiselectStatus, setMultiselectStatus] = useState<StatusType>('loading');
  const [overViewStatus, setOverViewStatus] = useState<StatusType>('loading');
  const [detailsStatus, setDetailsStatus] = useState<StatusType>('loading');

  //for Graphs
  const [appViewershipByTeamData, setAppViewershipByTeamData] = useState<MixedLineBarChartProps.BarDataSeries<string | number | Date>[]>([]);
  const [appViewershipByTeamXDomain, setAppViewershipByTeamXDomain] = useState<string[]>([]);
  const [appViewershipByTeamYDomain, setAppViewershipByTeamYDomain] = useState<number[]>([]);

  const [top5AppsViewership, setTop5AppsViewership] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [top5AppsViewershipXDomain, setTop5AppsViewershipXDomain] = useState<string[]>([]);
  const [top5AppsViewershipYDomain, setTop5AppsViewershipYDomain] = useState<number[]>([]);

  const [uniqueUsersByApplication, setUniqueUsersByApplication] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [uniqueUsersByApplicationXDomain, setUniqueUsersByApplicationXDomain] = useState<string[]>([]);
  const [uniqueUsersByApplicationYDomain, setUniqueUsersByApplicationYDomain] = useState<number[]>([]);

  const [totalApplicationViews, setTotalApplicationViews] = useState<MixedLineBarChartProps.LineDataSeries<string | number | Date>[]>([]);
  const [totalApplicationViewsXDomain, setTotalApplicationViewsXDomain] = useState<string[]>([]);
  const [totalApplicationViewsYDomain, setTotalApplicationViewsYDomain] = useState<number[]>([]);

  const [aggregatedViewsTableData, setAggregatedViewsTableData] = useState<UserMetricsAggregatedViewsTable>({} as UserMetricsAggregatedViewsTable);

  const [applicationViewsTableData, setApplicationViewsTableData] = useState<UserMetricsAggregatedViewsTable>({} as UserMetricsAggregatedViewsTable);

  const [aggregatedSubApplicationViewsTableData, setAggregatedSubApplicationViewsTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [aggregatedSubApplicationUniqueUsersTableData, setAggregatedSubApplicationUniqueUsersTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [applicationFootprintTableData, setApplicationFootprintTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [subApplicationFootprintTableData, setSubApplicationFootprintTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [applicationFootprintContainer, setApplicationFootprintContainer] = useState<UserMetricsApplicationFootprintContainer>(
    {} as UserMetricsApplicationFootprintContainer
  );

  const [applicationUserViewTableData, setApplicationUserViewTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [subApplicationUserViewTableData, setSubApplicationUserViewTableData] = useState<UserMetricsAggregatedViewsTable>(
    {} as UserMetricsAggregatedViewsTable
  );

  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  const [filteredUserMetricsData, setFilteredUserMetricsData] = useState<UserMetricsParsedData[]>([]);
  const [managerRosterMetricsData, setManagerRosterMetricsData] = useState<UserMetricsParsedData[]>([]);

  // Baseline master filters - set once on initial data load, used for reset and placeholder comparison
  const [baselineMasterFilters, setBaselineMasterFilters] = useState<UserMetricsMasterFilters>({} as UserMetricsMasterFilters);

  // Hierarchy tab shared state (persistent across "By Manager" and "By Application" tabs)
  const [isSubApplicationView, setIsSubApplicationView] = useState<boolean>(false);
  const [showViewsColumns, setShowViewsColumns] = useState<boolean>(false);
  const [selectedLevel, setSelectedLevel] = useState<number>(DEFAULT_MANAGER_LEVEL);

  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            {' '}
            submit a ticket
          </Link>
        );
      }
    }
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} 'or ' {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading,
        setIsLoading,
        isSplitPanelOpen,
        setIsSplitPanelOpen,
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        headerSelector="#h"
        navigation={<DFPHomePageSideNavigation />}
        toolsHide={true}
        notifications={<Flashbar items={flashbarItems} />}
        splitPanel={
          <SplitPanel
            header="Chart Summary"
            i18nStrings={{
              preferencesTitle: 'Split panel preferences',
              preferencesPositionLabel: 'Position',
              preferencesPositionDescription: 'Choose the default split panel position.',
              preferencesPositionSide: 'Side',
              preferencesPositionBottom: 'Bottom',
              preferencesConfirm: 'Confirm',
              preferencesCancel: 'Cancel',
              closeButtonAriaLabel: 'Close panel',
              openButtonAriaLabel: 'Open panel',
              resizeHandleAriaLabel: 'Resize split panel'
            }}
            closeBehavior="hide"
          >
            <ChartSummaryPanel />
          </SplitPanel>
        }
        onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
        splitPanelPreferences={{ position: 'side' }}
        splitPanelOpen={isSplitPanelOpen}
        splitPanelSize={400}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <div>
            <UserMetricsContext.Provider
              value={{
                displayNotification,

                multiselectStatus,
                setMultiselectStatus,

                overViewStatus,
                setOverViewStatus,

                detailsStatus,
                setDetailsStatus,

                appViewershipByTeamData,
                setAppViewershipByTeamData,
                appViewershipByTeamXDomain,
                setAppViewershipByTeamXDomain,
                appViewershipByTeamYDomain,
                setAppViewershipByTeamYDomain,

                top5AppsViewership,
                setTop5AppsViewership,
                top5AppsViewershipXDomain,
                setTop5AppsViewershipXDomain,
                top5AppsViewershipYDomain,
                setTop5AppsViewershipYDomain,

                uniqueUsersByApplication,
                setUniqueUsersByApplication,
                uniqueUsersByApplicationXDomain,
                setUniqueUsersByApplicationXDomain,
                uniqueUsersByApplicationYDomain,
                setUniqueUsersByApplicationYDomain,

                totalApplicationViews,
                setTotalApplicationViews,
                totalApplicationViewsXDomain,
                setTotalApplicationViewsXDomain,
                totalApplicationViewsYDomain,
                setTotalApplicationViewsYDomain,

                aggregatedViewsTableData: aggregatedViewsTableData,
                setAggregatedViewsTableData: setAggregatedViewsTableData,

                applicationUsersTableData: applicationViewsTableData,
                setApplicationUsersTableData: setApplicationViewsTableData,

                aggregatedSubApplicationViewsTableData: aggregatedSubApplicationViewsTableData,
                setAggregatedSubApplicationViewsTableData,

                aggregatedSubApplicationUniqueUsersTableData,
                setAggregatedSubApplicationUniqueUsersTableData,

                applicationFootprintTableData,
                setApplicationFootprintTableData,

                subApplicationFootprintTableData,
                setSubApplicationFootprintTableData,

                applicationFootprintContainer,
                setApplicationFootprintContainer,

                applicationUserViewTableData,
                setApplicationUserViewTableData,

                subApplicationUserViewTableData,
                setSubApplicationUserViewTableData,

                summaryFilters,
                setSummaryFilters,

                originalFilterOptions,
                setOriginalFilterOptions,

                filteredUserMetricsData,
                setFilteredUserMetricsData,
                managerRosterMetricsData,
                setManagerRosterMetricsData,

                baselineMasterFilters,
                setBaselineMasterFilters,

                isSubApplicationView,
                setIsSubApplicationView,
                showViewsColumns,
                setShowViewsColumns,
                selectedLevel,
                setSelectedLevel
              }}
            >
              <UserMetricsSelectionFilters />
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'userMetrics-OV',
                    content: <UserMetricsOverview />
                  },
                  {
                    label: 'Details',
                    id: 'userMetrics-DT',
                    content: <UserMetricsDetailsTab />
                  },
                  {
                    label: 'By Manager',
                    id: 'userMetrics-MHM',
                    content: <UserMetricsHierarchyMetricsTab hierarchyViewMode={HIERARCHY_VIEW_MODE.MANAGER} />
                  },
                  {
                    label: 'By Application',
                    id: 'userMetrics-AHM',
                    content: <UserMetricsHierarchyMetricsTab hierarchyViewMode={HIERARCHY_VIEW_MODE.APPLICATION} />
                  }
                ]}
              />
            </UserMetricsContext.Provider>
          </div>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
